import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/api";
import { useLang, } from "@/i18n";
import { useAuth } from "@/auth";
import Layout from "@/components/Layout";
import { ReferralCard } from "./ClientHome";
import { ClientLayout } from "./ClientHome";
import { toast } from "sonner";
import { Dumbbell, Check } from "lucide-react";
import { trackLead } from "@/analytics/metaPixel";

export default function Packages() {
  const { t } = useLang();
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState("");
  const [paypalEnabled, setPaypalEnabled] = useState(false);
  const [promo, setPromo] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    trackLead();
    api.get("/payments/packages").then((r) => setPackages(r.data));
    api.get("/payments/config").then((r) => setPaypalEnabled(!!r.data.paypal_enabled)).catch(() => {});
  }, []);

  useEffect(() => {
    const want = searchParams.get("buy");
    if (want && !loading && packages.some((p) => p.lookup_key === want)) {
      setSearchParams({}, { replace: true });
      buy(want);
    }
  }, [packages]);

  const buy = async (lookupKey) => {
    setLoading(lookupKey);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
        promo_code: promo.trim() || null,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setLoading("");
    }
  };

  const buyPaypal = async (lookupKey) => {
    setLoading(`pp_${lookupKey}`);
    try {
      const { data } = await api.post("/payments/paypal/checkout", {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
      });
      window.location.href = data.approval_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setLoading("");
    }
  };

  const expiry = user?.package_expires_at ? new Date(user.package_expires_at) : null;
  const valid = expiry && expiry > new Date();

  return (
    <ClientLayout>
      <div data-testid="packages-page">
        <h1 className="text-3xl font-black uppercase tracking-tight mb-3">{t("pk.title")}</h1>
        <input data-testid="promo-code-input" value={promo} onChange={(e) => setPromo(e.target.value)}
          placeholder={t("pk.promoPlaceholder")} className="kc-input mb-4 uppercase" />
        <p className="text-muted-foreground mb-8" data-testid="package-status">
          {t("pk.current")}: {valid
            ? `${t("pk.validUntil")} ${expiry.toLocaleDateString()}`
            : <span className="text-destructive font-semibold">{t("pk.none")}</span>}
        </p>
        <div className="mb-8"><ReferralCard /></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl">
          {packages.map((p, i) => (
            <div key={p.lookup_key} className={`kc-card p-6 flex flex-col ${i === 1 ? "border-primary/60" : ""}`} data-testid={`package-card-${p.lookup_key}`}>
              <Dumbbell className={`w-6 h-6 mb-3 ${i === 1 ? "text-primary" : "text-accent"}`} />
              <h3 className="font-bold text-lg">{p.name}</h3>
              <p className="font-mono text-4xl font-black my-4">€{(p.amount / 100).toFixed(0)}</p>
              <p className="text-xs text-muted-foreground -mt-3 mb-4" data-testid={`package-installments-${p.lookup_key}`}>
                {t("pk.installments").replace("{rate}", `€${(p.amount / 300).toFixed(0)}`)}
              </p>
              <p className="text-sm text-muted-foreground mb-4">{p.days} {t("pk.days")}</p>
              {p.features && (
                <ul className="space-y-2 mb-6 text-left" data-testid={`package-features-${p.lookup_key}`}>
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Check className="w-4 h-4 text-accent shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
              )}
              <button data-testid={`stripe-checkout-button-${p.lookup_key}`} onClick={() => buy(p.lookup_key)}
                disabled={!!loading} className="kc-btn-primary w-full !py-2.5 mt-auto">
                {loading === p.lookup_key ? t("common.loading") : t("pk.buy")}
              </button>
              {paypalEnabled && (
                <button data-testid={`paypal-checkout-button-${p.lookup_key}`} onClick={() => buyPaypal(p.lookup_key)}
                  disabled={!!loading}
                  className="w-full !py-2.5 mt-2 rounded-full border-2 border-[#003087] text-[#003087] font-bold text-sm hover:bg-[#003087] hover:text-white transition-colors">
                  {loading === `pp_${p.lookup_key}` ? t("common.loading") : t("pk.buyPaypal")}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </ClientLayout>
  );
}
